import {
  Links,
  LoaderFunctionArgs,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
  Meta,
  useLocation,
  LinksFunction,
  redirect
} from 'react-router'
import {Toaster, TooltipProvider} from '@translated/babele-react'
import {useEffect} from 'react'
import {loadGtmScript, loadMetaPixelScript, loadLinkedInPixelScript} from '~/utils/scripts'
import {getLocalizedPath, getPathDataFromRequest} from '~/utils/routes'
import {getAppSearchParams, getWebsiteSearchParams, getSearchStringWithoutParams} from './utils/url'
import {HubspotLauncher} from './features/HubspotLauncher/HubspotLauncher'
import {dir as getLangDirection} from 'i18next'
import {useTranslation} from 'react-i18next'
import {DirectionProvider} from '@radix-ui/react-direction'
import {checkAuth} from './utils/auth'
import {SupportedLanguage} from './utils/locales'
import {ROOT_LINKS} from '~/utils/links'
import {envVars} from './utils/env.server'
import TrackingPixel from './features/TrackingPixel'

export const links: LinksFunction = () => ROOT_LINKS

export const loader = ({request}: LoaderFunctionArgs) => {
  const url = new URL(request.url)
  const {search} = url

  const pathData = getPathDataFromRequest(request)

  const isAuthenticated = checkAuth(request)
  const {lng: languageFromSearch} = getWebsiteSearchParams(search)
  const {sourceApp} = getAppSearchParams(search)

  const loaderData = {
    pathData,
    isAuthenticated,
    comingFromMymemory: sourceApp === 'mymemory',
    env: {
      isHubspotEnabled: envVars.VITE_IS_HUBSPOT_ENABLED,
      gaTrackingId: envVars.VITE_GA_TRACKING_ID,
      laraAppUrl: envVars.VITE_LARA_APP_URL,
      baseUrl: envVars.VITE_BASE_URL,
      metaPixelId: envVars.VITE_META_PIXEL_ID,
      linkedInPixelId: envVars.VITE_LINKEDIN_PIXEL_ID
    }
  }

  if (!languageFromSearch) return loaderData

  const localizedPath = getLocalizedPath({
    path: pathData.currentPath,
    language: languageFromSearch as SupportedLanguage
  })

  if (localizedPath) {
    const searchString = getSearchStringWithoutParams(search, ['lng'])
    return redirect(`${encodeURI(localizedPath)}${searchString}`)
  }

  // If no localizedPath found, return loader data with the language from search params
  return {
    ...loaderData,
    pathData: {
      ...pathData,
      language: languageFromSearch as SupportedLanguage
    }
  }
}

export function Layout({children}: {children: React.ReactNode}) {
  const {pathData, comingFromMymemory, env} = useRouteLoaderData<typeof loader>('root') || {}

  const location = useLocation()
  const skipPageIndexing = !!getAppSearchParams(location.search).text

  useEffect(() => {
    if (env?.gaTrackingId != null) loadGtmScript(env.gaTrackingId)
    if (env?.metaPixelId != null) loadMetaPixelScript(env.metaPixelId)
    if (env?.linkedInPixelId != null) loadLinkedInPixelScript(env.linkedInPixelId)
  }, [env?.gaTrackingId, env?.metaPixelId, env?.linkedInPixelId])

  return (
    <html
      data-theme="translated-light"
      data-panda-theme="translated"
      data-panda-mode="light"
      lang={pathData?.language}
      dir={getLangDirection(pathData?.language)}
      className="bg-[#4445B8] text-light-icons-primary 4xl:text-[18px] 5xl:text-[32px]"
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="manifest" href="/manifest.json" />
        {skipPageIndexing && <meta name="robots" content="noindex, follow" />}
        <link rel="canonical" href={`${env?.baseUrl}${pathData?.pathname}`} />
        <Links />
        <Meta />
        {env?.metaPixelId && (
          <TrackingPixel
            src={`https://www.facebook.com/tr?id=${env.metaPixelId}&ev=PageView&noscript=1`}
          />
        )}
      </head>
      <body className="bg-laraFull relative flex min-h-screen flex-col items-stretch text-neutral-1000">
        <TooltipProvider>{children}</TooltipProvider>
        <Toaster position="top-right" />
        <ScrollRestoration />
        <Scripts />
        {env?.isHubspotEnabled && !comingFromMymemory && <HubspotLauncher />}
        {env?.linkedInPixelId && (
          <TrackingPixel
            src={`https://px.ads.linkedin.com/collect/?pid=${env.linkedInPixelId}&fmt=gif`}
          />
        )}
      </body>
    </html>
  )
}

export default function App() {
  const {i18n} = useTranslation()

  return (
    <DirectionProvider dir={i18n.dir()}>
      <Outlet />
    </DirectionProvider>
  )
}

export {ErrorBoundary} from '~/features/RootErrorBoundary'
